import { useCallback, useState, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import {
	updateScannerRequestParam,
	uploadExamFile,
} from "../../../components/Api/ExamGradingEndpoints";
import {
	validateHeader,
	generateCorrectedFile,
	createCorrectedFile,
} from "../utils/parseRawExamFile";

/**
 * FileValidationDialog - Handles both file parsing validation and database reconciliation
 *
 * Flow:
 * 1. File is parsed on frontend
 * 2. This dialog opens showing parsed values and any validation issues
 * 3. User can edit header fields if needed
 * 4. If no issues (or after fixing), compare with database values
 * 5. Handle any database mismatches (existing ReconciliationDialog behavior)
 * 6. Proceed with upload to backend
 */
export default function FileValidationDialog({
	open,
	setOpen,
	parsedData,
	setParsedData,
	originalFileContent,
	file,
	token,
	ccid,
	currentRequest,
	fetchRequestDetails,
	onUploadComplete,
	setSnackbar,
	uploadMode = "upsert",
}) {
	const [activeTab, setActiveTab] = useState(0);
	const [loading, setLoading] = useState(false);
	const [editedHeader, setEditedHeader] = useState({});
	const [reconciliationState, setReconciliationState] = useState([]);
	const [showReconciliation, setShowReconciliation] = useState(false);
	const [validationIssues, setValidationIssues] = useState([]);

	// Initialize edited header from parsed data
	useEffect(() => {
		if (parsedData?.header) {
			setEditedHeader({ ...parsedData.header });
			const issues = validateHeader(parsedData.header);
			setValidationIssues(issues);
		}
	}, [parsedData?.header]);

	// Reset state when dialog opens/closes
	useEffect(() => {
		if (!open) {
			setActiveTab(0);
			setReconciliationState([]);
			setShowReconciliation(false);
		}
	}, [open]);

	const handleClose = useCallback(() => {
		setOpen(false);
	}, [setOpen]);

	// Count errors and warnings
	const errorCount = useMemo(() => {
		const parseErrors = parsedData?.errors?.length || 0;
		const validationErrors = validationIssues.filter((i) => i.severity === "error").length;
		return parseErrors + validationErrors;
	}, [parsedData?.errors, validationIssues]);

	const warningCount = useMemo(() => {
		const parseWarnings = parsedData?.warnings?.length || 0;
		const validationWarnings = validationIssues.filter((i) => i.severity === "warning").length;
		return parseWarnings + validationWarnings;
	}, [parsedData?.warnings, validationIssues]);

	// Handle header field edit
	const handleHeaderFieldChange = useCallback((field, value) => {
		setEditedHeader((prev) => ({
			...prev,
			[field]: value,
		}));
	}, []);

	// Revalidate after edits
	useEffect(() => {
		if (Object.keys(editedHeader).length > 0) {
			const issues = validateHeader(editedHeader);
			setValidationIssues(issues);
		}
	}, [editedHeader]);

	// Download corrected file
	const handleDownloadCorrectedFile = useCallback(() => {
		if (!originalFileContent) return;

		const correctedContent = generateCorrectedFile(
			editedHeader,
			parsedData?.header, // original header for rawHeaders
			originalFileContent,
		);

		const blob = new Blob([correctedContent], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `corrected_${file?.name || "exam_file.txt"}`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}, [editedHeader, parsedData?.header, originalFileContent, file]);

	const maybeSurfaceUploadWarnings = useCallback(
		(uploadResponse) => {
			const duplicates = uploadResponse?.summary?.duplicate_std_id_raws;
			if (!Array.isArray(duplicates) || duplicates.length === 0) return;

			const preview = duplicates
				.slice(0, 3)
				.map((entry) => `${entry.std_id_raw} (${entry.count})`)
				.join(", ");
			const remainingCount = duplicates.length - Math.min(duplicates.length, 3);
			const message = `Warning: ${duplicates.length} raw student ID${
				duplicates.length === 1 ? "" : "s"
			} appear multiple times in this exam after upload: ${preview}${
				remainingCount > 0 ? `, +${remainingCount} more` : ""
			}.`;

			setSnackbar?.({
				open: true,
				message,
				severity: "warning",
			});
		},
		[setSnackbar],
	);

	// Proceed with upload to backend - sends CORRECTED file, not original
	const handleProceedWithUpload = async () => {
		setLoading(true);
		try {
			// Create corrected file with the edited header values
			const correctedFile = createCorrectedFile(
				editedHeader,
				parsedData?.header, // original header for rawHeaders
				originalFileContent,
				file?.name,
			);

			// Upload the CORRECTED file, not the original
			const parseFileResponse = await uploadExamFile(token, correctedFile, uploadMode, ccid);

			if (parseFileResponse.error) {
				// Database reconciliation needed
				setReconciliationState(parseFileResponse.mismatches || []);
				setShowReconciliation(true);
				setActiveTab(1);
			} else {
				// Success - no reconciliation needed
				await fetchRequestDetails();
				onUploadComplete?.(parseFileResponse);
				setOpen(false);
				maybeSurfaceUploadWarnings(parseFileResponse);
			}
		} catch (error) {
			console.error("Upload error:", error);
			setSnackbar?.({
				open: true,
				message: `Upload failed: ${error.message || "Unknown error"}`,
				severity: "error",
			});
		}
		setLoading(false);
	};

	// Handle resolving a database mismatch
	const handleReconcileMismatchedValue = async (reqnum, mismatch, expected, got) => {
		try {
			const response = await updateScannerRequestParam(
				token,
				ccid,
				reqnum,
				mismatch,
				expected,
				got,
			);
			setReconciliationState((prevState) =>
				prevState.filter((item) => item.mismatch !== response.data.mismatch),
			);
		} catch (error) {
			console.error("Error during reconciliation:", error);
			setSnackbar?.({
				open: true,
				message: `Reconciliation failed: ${error.message || "Unknown error"}`,
				severity: "error",
			});
		}
	};

	// Final continue after reconciliation - also uses corrected file
	const handleContinueAfterReconciliation = async () => {
		setLoading(true);
		try {
			// Create corrected file with the edited header values
			const correctedFile = createCorrectedFile(
				editedHeader,
				parsedData?.header,
				originalFileContent,
				file?.name,
			);

			const parseFileResponse = await uploadExamFile(token, correctedFile, uploadMode, ccid);
			if (parseFileResponse.error) {
				setReconciliationState(parseFileResponse.mismatches || []);
			} else {
				await fetchRequestDetails();
				onUploadComplete?.(parseFileResponse);
				setOpen(false);
				maybeSurfaceUploadWarnings(parseFileResponse);
			}
		} catch (error) {
			console.error("Error:", error);
		}
		setLoading(false);
	};

	// Check if we can proceed (no blocking errors)
	const canProceed = errorCount === 0;

	// Check if editedHeader has been populated from parsedData
	const isEditedHeaderReady = Object.keys(editedHeader).length > 0 && editedHeader.reqnum;

	// Check if there are outstanding reconciliation mismatches
	const hasOutstandingMismatches = showReconciliation && reconciliationState.length > 0;

	const HEADER_FIELDS = [
		{ key: "reqnum", label: "Request Number", editable: false },
		{ key: "lqnum", label: "Low Question #", editable: true },
		{ key: "hqnum", label: "High Question #", editable: true },
		{ key: "qlen", label: "Question Length", editable: true },
		{ key: "nkeys", label: "Number of Keys", editable: true },
		{ key: "sheet_type", label: "Sheet Type", editable: false },
		{ key: "coursetitle", label: "Course Title", editable: false },
	];

	const SPEC_FIELDS = ["a", "b", "c", "d", "e", "f", "k", "l", "m", "n", "o", "p"];
	const getAnswerPreview = useCallback((rawAnswers) => {
		const text = String(rawAnswers || "");
		if (!text) return "";

		const firstVisibleIndex = text.search(/\S/);
		if (firstVisibleIndex <= 0) {
			return text.substring(0, 50) + (text.length > 50 ? "..." : "");
		}

		const preview = text.substring(firstVisibleIndex, firstVisibleIndex + 50);
		return `[${firstVisibleIndex} leading blanks] ${preview}${
			firstVisibleIndex + 50 < text.length ? "..." : ""
		}`;
	}, []);

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason !== "backdropClick" && !loading) {
					handleClose();
				}
			}}
			aria-labelledby="file-validation-dialog-title"
			fullWidth
			maxWidth="lg"
			disableEscapeKeyDown={loading}
		>
			<DialogTitle id="file-validation-dialog-title">
				<Box display="flex" alignItems="center" justifyContent="space-between">
					<Box display="flex" alignItems="center" gap={2}>
						<Typography variant="h6">File Validation</Typography>
						{errorCount > 0 && (
							<Chip
								icon={<ErrorIcon />}
								label={`${errorCount} Error${errorCount > 1 ? "s" : ""}`}
								color="error"
								size="small"
							/>
						)}
						{warningCount > 0 && (
							<Chip
								icon={<WarningAmberIcon />}
								label={`${warningCount} Warning${warningCount > 1 ? "s" : ""}`}
								color="warning"
								size="small"
							/>
						)}
						{errorCount === 0 && warningCount === 0 && (
							<Chip icon={<CheckCircleIcon />} label="Valid" color="success" size="small" />
						)}
					</Box>
					<Typography variant="body2" color="text.secondary">
						REQ: <strong>{parsedData?.header?.reqnum || "N/A"}</strong>
					</Typography>
				</Box>
			</DialogTitle>

			<DialogContent>
				<Tabs
					value={activeTab}
					onChange={(e, newValue) => setActiveTab(newValue)}
					sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
				>
					<Tab label="File Validation" />
					<Tab label="Database Reconciliation" disabled={!showReconciliation} />
					<Tab label="Summary" />
				</Tabs>

				{/* Tab 0: File Validation */}
				{activeTab === 0 && (
					<Box>
						{/* Parsing Errors */}
						{parsedData?.errors?.length > 0 && (
							<Alert severity="error" sx={{ mb: 2 }}>
								<AlertTitle>Parsing Errors</AlertTitle>
								<ul style={{ margin: 0, paddingLeft: 20 }}>
									{parsedData.errors.map((err, idx) => (
										<li key={idx}>
											{err.line && `Line ${err.line}: `}
											{err.message}
										</li>
									))}
								</ul>
							</Alert>
						)}

						{/* Validation Issues */}
						{validationIssues.filter((i) => i.severity === "error").length > 0 && (
							<Alert severity="error" sx={{ mb: 2 }}>
								<AlertTitle>Validation Errors</AlertTitle>
								<ul style={{ margin: 0, paddingLeft: 20 }}>
									{validationIssues
										.filter((i) => i.severity === "error")
										.map((issue, idx) => (
											<li key={idx}>
												<strong>{issue.field}</strong>: {issue.message}
												{issue.value && ` (value: "${issue.value}")`}
											</li>
										))}
								</ul>
							</Alert>
						)}

						{/* Warnings */}
						{(parsedData?.warnings?.length > 0 ||
							validationIssues.filter((i) => i.severity === "warning").length > 0) && (
							<Alert severity="warning" sx={{ mb: 2 }}>
								<AlertTitle>Warnings</AlertTitle>
								<ul style={{ margin: 0, paddingLeft: 20 }}>
									{parsedData?.warnings?.map((warn, idx) => (
										<li key={`pw-${idx}`}>{warn.message}</li>
									))}
									{validationIssues
										.filter((i) => i.severity === "warning")
										.map((issue, idx) => (
											<li key={`vi-${idx}`}>
												<strong>{issue.field}</strong>: {issue.message}
											</li>
										))}
								</ul>
							</Alert>
						)}

						{/* Header Fields */}
						<Accordion defaultExpanded>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography variant="subtitle1" fontWeight="bold">
									Header Fields
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<TableContainer component={Paper} variant="outlined">
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontWeight: "bold" }}>Field</TableCell>
												<TableCell sx={{ fontWeight: "bold" }}>Value</TableCell>
												<TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{HEADER_FIELDS.map((field) => {
												const issue = validationIssues.find(
													(i) =>
														i.field === field.key ||
														(field.key === "lqnum" && i.field === "question_range") ||
														(field.key === "hqnum" && i.field === "question_range"),
												);
												return (
													<TableRow key={field.key}>
														<TableCell>{field.label}</TableCell>
														<TableCell>
															{field.editable ? (
																<TextField
																	size="small"
																	disabled={loading}
																	value={editedHeader[field.key] || ""}
																	onChange={(e) =>
																		handleHeaderFieldChange(field.key, e.target.value)
																	}
																	error={issue?.severity === "error"}
																	sx={{ width: 150 }}
																/>
															) : (
																<Typography variant="body2">
																	{editedHeader[field.key] || "N/A"}
																</Typography>
															)}
														</TableCell>
														<TableCell>
															{issue ? (
																<Chip
																	size="small"
																	label={issue.severity}
																	color={issue.severity === "error" ? "error" : "warning"}
																/>
															) : (
																<Chip size="small" label="OK" color="success" />
															)}
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							</AccordionDetails>
						</Accordion>

						{/* Exam Specification Fields */}
						<Accordion defaultExpanded>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography variant="subtitle1" fontWeight="bold">
									Exam Specification (a-p flags)
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
									These flags control exam processing options. Each should be a single digit (0-9).
								</Typography>
								<Box display="flex" flexWrap="wrap" gap={1}>
									{SPEC_FIELDS.map((field) => (
										<Box key={field} display="flex" alignItems="center" gap={0.5}>
											<Typography variant="body2" fontWeight="bold" sx={{ minWidth: 20 }}>
												{field.toUpperCase()}:
											</Typography>
											<TextField
												size="small"
												disabled={loading}
												value={editedHeader[field] || "0"}
												onChange={(e) => {
													// Only allow single digit
													const val = e.target.value.replace(/[^0-9]/g, "").substring(0, 1);
													handleHeaderFieldChange(field, val || "0");
												}}
												InputProps={{
													sx: { fontFamily: "monospace", textAlign: "center" },
												}}
												sx={{ width: 45 }}
											/>
										</Box>
									))}
								</Box>
							</AccordionDetails>
						</Accordion>

						{/* Answer Keys Summary */}
						<Accordion>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography variant="subtitle1" fontWeight="bold">
									Answer Keys ({parsedData?.answerKeys?.length || 0})
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								{parsedData?.answerKeys?.length > 0 ? (
									<TableContainer component={Paper} variant="outlined">
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
													<TableCell sx={{ fontWeight: "bold" }}>Name/Version</TableCell>
													<TableCell sx={{ fontWeight: "bold" }}>Answer Key (preview)</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{parsedData.answerKeys.map((key, idx) => (
													<TableRow key={idx}>
														<TableCell>{idx + 1}</TableCell>
														<TableCell>{key.name || key.data?.special_code || "Key"}</TableCell>
														<TableCell>
															<Typography
																variant="body2"
																sx={{ fontFamily: "monospace", fontSize: 12 }}
															>
																{getAnswerPreview(key.data?.answers_chosen)}
															</Typography>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								) : (
									<Typography color="text.secondary">No answer keys parsed.</Typography>
								)}
							</AccordionDetails>
						</Accordion>

						{/* Student Responses Summary */}
						<Accordion>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography variant="subtitle1" fontWeight="bold">
									Student Responses ({parsedData?.studentResponses?.length || 0})
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								{parsedData?.studentResponses?.length > 0 ? (
									<Typography variant="body2">
										{parsedData.studentResponses.length} student response
										{parsedData.studentResponses.length !== 1 ? "s" : ""} parsed successfully.
									</Typography>
								) : (
									<Typography color="text.secondary">No student responses parsed.</Typography>
								)}
							</AccordionDetails>
						</Accordion>
					</Box>
				)}

				{/* Tab 1: Database Reconciliation */}
				{activeTab === 1 && (
					<Box>
						{reconciliationState.length > 0 ? (
							<>
								<Alert severity="warning" sx={{ mb: 2 }}>
									<AlertTitle>Database Mismatch</AlertTitle>
									The following values in the file differ from the database values. Please resolve
									each mismatch to continue.
								</Alert>
								<TableContainer component={Paper} variant="outlined">
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontWeight: "bold" }}>Field</TableCell>
												<TableCell sx={{ fontWeight: "bold" }}>Database Value</TableCell>
												<TableCell sx={{ fontWeight: "bold" }}>File Value</TableCell>
												<TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{reconciliationState.map((item, index) => (
												<TableRow key={index}>
													<TableCell>
														<strong>{item.mismatch}</strong>
													</TableCell>
													<TableCell>{item.expected}</TableCell>
													<TableCell sx={{ color: "error.main" }}>{item.got}</TableCell>
													<TableCell>
														<Button
															variant="contained"
															color="primary"
															size="small"
															onClick={() =>
																handleReconcileMismatchedValue(
																	parsedData?.header?.reqnum,
																	item.mismatch,
																	item.expected,
																	item.got,
																)
															}
														>
															Use File Value
														</Button>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</>
						) : showReconciliation ? (
							<Alert severity="success">
								<AlertTitle>All Mismatches Resolved</AlertTitle>
								All database mismatches have been resolved. Click &quot;Continue Processing&quot; to
								upload the exam data.
							</Alert>
						) : (
							<Typography color="text.secondary">
								Database reconciliation will be checked after file validation passes.
							</Typography>
						)}
					</Box>
				)}

				{/* Tab 2: Summary */}
				{activeTab === 2 && (
					<Box>
						<Alert
							severity={errorCount > 0 ? "error" : warningCount > 0 ? "warning" : "success"}
							sx={{ mb: 2 }}
						>
							<AlertTitle>
								{errorCount > 0
									? "Cannot Proceed - Errors Found"
									: warningCount > 0
										? "Ready to Proceed - With Warnings"
										: "Ready to Proceed"}
							</AlertTitle>
							<Typography variant="body2">
								<strong>Request Number:</strong> {parsedData?.header?.reqnum || "N/A"}
								<br />
								<strong>Questions:</strong> {parsedData?.header?.lqnum || "?"} -{" "}
								{parsedData?.header?.hqnum || "?"} (
								{(parseInt(parsedData?.header?.hqnum, 10) || 0) -
									(parseInt(parsedData?.header?.lqnum, 10) || 0) +
									1}{" "}
								total)
								<br />
								<strong>Answer Keys:</strong> {parsedData?.answerKeys?.length || 0}
								<br />
								<strong>Student Responses:</strong> {parsedData?.studentResponses?.length || 0}
								<br />
								<strong>Sheet Type:</strong> {parsedData?.header?.sheet_type || "Unknown"}
								<br />
								<strong>Weighted Exam:</strong> {parsedData?.header?.weighted ? "Yes" : "No"}
							</Typography>
						</Alert>

						<Divider sx={{ my: 2 }} />

						<Typography variant="subtitle2" gutterBottom>
							File: {file?.name || "Unknown"}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Original file is preserved. A corrected copy with your edits will be sent to the
							server.
						</Typography>
					</Box>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
				<Box>
					<Button
						startIcon={<DownloadIcon />}
						onClick={handleDownloadCorrectedFile}
						disabled={loading}
					>
						Download Modified File
					</Button>
				</Box>
				<Box display="flex" gap={1}>
					{showReconciliation && reconciliationState.length === 0 ? (
						<Button
							variant="contained"
							color="primary"
							onClick={handleContinueAfterReconciliation}
							disabled={loading}
						>
							{loading ? <CircularProgress size={24} /> : "Continue Processing"}
						</Button>
					) : (
						<>
							<Button onClick={handleClose} disabled={loading}>
								Cancel
							</Button>
							<Button
								variant="contained"
								color="primary"
								onClick={handleProceedWithUpload}
								disabled={!canProceed || loading || hasOutstandingMismatches}
							>
								{loading ? (
									<CircularProgress size={24} />
								) : hasOutstandingMismatches ? (
									"Resolve Mismatches First"
								) : canProceed ? (
									"Validate & Upload"
								) : (
									"Fix Errors First"
								)}
							</Button>
						</>
					)}
				</Box>
			</DialogActions>
		</Dialog>
	);
}
